import React from "react";
import PopupComponent from "@components/PopupComponent";
import { FIRST_ELEMENT, SECOND_ELEMENT } from "./constants";

/**
 * Reusable ChildTable component for displaying child records in a table format.
 * Can be used in both OutstandingRecords and ExceptionDetails.
 * 
 * @param {Array} data - Array of child records to display
 * @param {Array} childrenColumns - Column configuration array
 * @param {Object} selectedRows - Object tracking selected row IDs
 * @param {Function} setSelectedRows - Function to update selected rows
 * @param {string|number} popupOpen - ID of the currently open popup (or null)
 * @param {Function} setPopupOpen - Function to update which popup is open
 */
const SharedChildTable = ({
  data,
  childrenColumns,
  selectedRows,
  setSelectedRows,
  popupOpen,
  setPopupOpen,
}) => {
  return (
    <div className="relative max-h-[230px] overflow-y-auto pl-5">
      <table className="dark:bg-gray-600 dark:text-white bg-white text-center border-gray-200 relative min-w-max">
        <thead>
          <tr className="dark:bg-gray-600  bg-white text-15 text-center border-b-2 sticky top-0 w-full shadow-[0px 0px 1px #e1e1e1]">
            <th width={50}></th>
            {childrenColumns.map((column, index) => {
              return (
                <th
                  key={index}
                  style={{ width: `${parseInt(column.size)}px` }}
                  className={`py-[10px] dark:text-white px-[10px] whitespace-nowrap text-xs border-r-0 border-gray-100 font-normal text-gray-400 text-${column.alignment}`}
                >
                  {!column.hideHeader && column.header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="">
          {data.map((child, index) => {
            const checked = selectedRows[child.PromotedRecordId];
            return (
              <tr
                key={index}
                className="text-secondary-dark hover:bg-bg-2 border-b border-line last:border-none"
              >
                <td width={50}>
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={checked}
                    onClick={() => {
                      setSelectedRows((prev) => {
                        const newState = { ...prev };

                        if (
                          newState.hasOwnProperty(child.PromotedRecordId)
                        ) {
                          if (checked) {
                            delete newState[child.PromotedRecordId];
                          }
                        } else {
                          newState[child.PromotedRecordId] = true;
                        }

                        return newState;
                      });
                    }}
                  />
                </td>
                {childrenColumns.map((column, index) => {
                  let cellValue = child[column.accessorKey];

                  return (
                    <td
                      key={index}
                      style={{ width: `${parseInt(column.size)}px` }}
                      className={`py-[10px] px-[10px] border-r-0 border-gray-100 text-${column.alignment}`}
                    >
                      {column.displayInPopup ? (
                        <div className="flex justify-center items-center">
                          {popupOpen === child.PromotedRecordId && (
                            <PopupComponent
                              open={popupOpen === child.PromotedRecordId}
                              rawValue={column.popupContent(child)}
                              setPopupOpen={setPopupOpen}
                              addBlur
                            />
                          )}

                          <span
                            className="cursor-pointer"
                            onClick={() => {
                              setPopupOpen(child.PromotedRecordId);
                            }}
                          >
                            {column.popupCellDisplay(cellValue)}
                          </span>
                        </div>
                      ) : column.splitCellValue ? (
                        <span className="text-sm">
                          {
                            cellValue?.split(column.splitCellValue)[
                              FIRST_ELEMENT
                            ]
                          }
                          <br />
                          {
                            cellValue?.split(column.splitCellValue)[
                              SECOND_ELEMENT
                            ]
                          }
                        </span>
                      ) : (
                        (typeof cellValue === "string" || column.cell) &&
                        (column.cell
                          ? column.cell({ row: { original: child } })
                          : cellValue)
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SharedChildTable;
